<script setup lang="ts">
  import { Icon, IconName, IconSize, Input, Typo, TypoVariant } from '@travelata-libs/ui-kit';
  import { computed, ref, watch } from 'vue';
  import type { DestinationPrices } from '@/entities/searchCriteria';
  import { Field, FieldSize } from '@/entities/searchForm';
  import { useOutsidePointer } from '@/shared/composables';
  import { type Country, type Destination } from '@/shared/model';
  import DestinationDropdown from './DestinationDropdown.vue';
  import { DESTINATION_TEXTS } from '../config/texts';
  import { emptySelection, isCountry, type Selection } from '../model/selection';
  import { useDestination } from '../model/useDestination';

  interface DestinationFieldProps {
    modelValue?: Selection | null;
    emptyByDefault?: boolean;
    countries?: Country[];
    disabled?: boolean;
    prices?: DestinationPrices;
    size?: `${FieldSize}`;
    loading?: boolean;
    searchUrl?: string;
    resortsUrl?: string;
  }

  const props = withDefaults(defineProps<DestinationFieldProps>(), {
    modelValue: undefined,
    emptyByDefault: false,
    countries: () => [],
    disabled: false,
    prices: undefined,
    size: FieldSize.M,
    loading: false,
    searchUrl: '',
    resortsUrl: '',
  });

  interface DestinationFieldEmits {
    (e: 'update:modelValue', selection: Selection): void;
  }

  const emit = defineEmits<DestinationFieldEmits>();

  const EMPTY_SELECTION = emptySelection();

  const model = computed<Selection>({
    get: () => props.modelValue ?? EMPTY_SELECTION,
    set: (value) => emit('update:modelValue', value),
  });

  const destination = useDestination({
    model,
    initialCountries: props.countries ?? [],
    prices: () => props.prices,
    searchUrl: props.searchUrl,
    resortsUrl: props.resortsUrl,
  });

  watch(
    () => props.countries,
    (list) => destination.setCountries(list ?? []),
  );

  watch(
    () => props.prices,
    () => {
      destination.setCountries(props.countries ?? []);
      destination.repriceResorts();
    },
    { deep: true },
  );

  const showSelectAll = computed<boolean>(() => destination.selectAllVisible.value);

  const isCountrySelected = computed<boolean>(() => isCountry(destination.selection.value));

  const inputRef = ref<{ focus: () => void } | null>(null);

  const focusInput = (): void => {
    inputRef.value?.focus();
  };

  const onOpen = (): void => {
    destination.open();
  };

  const onSelect = (option: Destination): void => {
    destination.selectDestination(option);
  };

  const onKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      destination.close();
    }
  };

  const onClear = (): void => {
    destination.clearInput();
    focusInput();
  };

  const rootRef = ref<HTMLElement | null>(null);

  useOutsidePointer(rootRef, { active: destination.controlActivated, handler: destination.close });

  defineExpose({
    validate: (): boolean => destination.validate(),
    clear: (): void => destination.clear(),
  });
</script>

<script lang="ts">
  export default {
    model: {
      prop: 'modelValue',
      event: 'update:modelValue',
    },
  };
</script>

<template>
  <div
    ref="rootRef"
    class="destination-field"
    data-testid="destination-field"
  >
    <DestinationDropdown
      :options="destination.optionsList.value"
      :selection="destination.selection.value"
      :loading="destination.loading.value"
      :visible="destination.controlActivated.value"
      :show-select-all="showSelectAll"
      :select-all-checked="isCountrySelected"
      :select-all-country="destination.selectedCountry.value"
      @select="onSelect"
      @select-all="destination.toggleSelectAll"
      @close="destination.close"
    >
      <template #reference>
        <Field
          variant="input"
          :label="DESTINATION_TEXTS.fieldLabelFixed"
          :active="destination.controlActivated.value"
          :loading="loading"
          :disabled="disabled"
          :size="size"
          @click="focusInput"
        >
          <template #value>
            <Input
              ref="inputRef"
              :model-value="destination.inputString.value"
              :placeholder="DESTINATION_TEXTS.placeholder"
              :error="destination.invalid.value"
              :bg="false"
              :disabled="disabled"
              :bordered="false"
              name="input"
              size="xs"
              data-testid="destination-input"
              class="destination-field__input"
              @update:modelValue="destination.setInput"
              @focus="onOpen"
              @click="onOpen"
              @keydown.native="onKeydown"
            >
              <template #append>
                <button
                  v-if="destination.clearVisible.value"
                  type="button"
                  class="destination-field__clear"
                  data-testid="destination-clear"
                  aria-label="Очистить"
                  @click.stop.prevent="onClear"
                >
                  <Icon
                    :name="IconName.Close"
                    :size="IconSize.S"
                  />
                </button>
              </template>
            </Input>
          </template>
        </Field>
      </template>
    </DestinationDropdown>

    <div
      v-if="destination.errorText.value"
      class="destination-field__error"
      data-testid="destination-error"
    >
      <Typo
        :variant="TypoVariant.BodySRegular"
        tag="span"
      >
        {{ destination.errorText.value }}
      </Typo>
    </div>
  </div>
</template>

<style scoped lang="scss">
  ::v-deep .destination-field__input input[name='input'] {
    font-size: 16px;
  }

  .destination-field {
    position: relative;
    width: 100%;

    ::v-deep .tl-input {
      padding: 0;
    }

    &__clear {
      align-items: center;
      background: none;
      border: none;
      color: var(--color-text-secondary);
      cursor: pointer;
      display: inline-flex;
      justify-content: center;
      padding: 0;
    }

    &__error {
      color: var(--color-error-500);
      margin-top: var(--spacing-2xs);
    }
  }
</style>
